import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../Firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { ADMIN_EMAILS } from './adminApi';
import SubmissionsTab from './SubmissionsTab';
import ActivityTab from './ActivityTab';
import NewSignupsTab from './NewSignupsTab';
import ManageCommunitiesTab from './ManageCommunitiesTab';
import ABTestTab from './ABTestTab';
import CommunityMembersTab from './CommunityMembersTab';
import EmailManagerTab from './EmailManagerTab';
import CommunityAnalyticsTab from './CommunityAnalyticsTab';
import QuickMatchWaitlistTab from './QuickMatchWaitlistTab';
import CommunityStatsTab from './CommunityStatsTab';
import './Admin.css';

const Admin = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('submissions');
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const tabsRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return unsub;
  }, []);

  const updateScrollEdges = () => {
    const el = tabsRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollEdges();
    const el = tabsRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollEdges, { passive: true });
    window.addEventListener('resize', updateScrollEdges);
    return () => {
      el.removeEventListener('scroll', updateScrollEdges);
      window.removeEventListener('resize', updateScrollEdges);
    };
  }, [user]);

  const scrollTabs = (dir) => {
    tabsRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  if (loading) return <div className="as-loading">Loading...</div>;

  if (!user) return (
    <div className="as-gate">
      <h2>Admin access</h2>
      <p>Please log in with the admin account to continue.</p>
    </div>
  );

  if (!ADMIN_EMAILS.includes(user.email)) return (
    <div className="as-gate">
      <h2>Access denied</h2>
      <p>This page is restricted to administrators.</p>
    </div>
  );

  return (
    <div className="as-root">
      <div className="as-header">
        <h1 className="as-title">Admin</h1>
        <div className={`as-filter-tabs-wrap ${atStart ? 'as-filter-tabs-wrap--at-start' : ''} ${atEnd ? 'as-filter-tabs-wrap--at-end' : ''}`}>
          <button type="button" className="as-tabs-arrow as-tabs-arrow--left" onClick={() => scrollTabs(-1)} disabled={atStart} aria-label="Scroll tabs left">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div className="as-filter-tabs-scroller">
            <div className="as-filter-tabs" ref={tabsRef}>
              <button className={`as-filter-tab ${tab === 'email' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('email')}>Email Manager</button>
              <button className={`as-filter-tab ${tab === 'submissions' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('submissions')}>Submissions</button>
              <button className={`as-filter-tab ${tab === 'activity' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('activity')}>Activity</button>
              <button className={`as-filter-tab ${tab === 'pageAnalytics' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('pageAnalytics')}>Page Analytics</button>
              <button className={`as-filter-tab ${tab === 'communityStats' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('communityStats')}>Community Analytics</button>
              <button className={`as-filter-tab ${tab === 'signups' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('signups')}>New Signups</button>
              <button className={`as-filter-tab ${tab === 'waitlist' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('waitlist')}>Quick-match Emails</button>
              <button className={`as-filter-tab ${tab === 'communities' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('communities')}>Manage Communities</button>
              <button className={`as-filter-tab ${tab === 'members' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('members')}>Community Members</button>
              <button className={`as-filter-tab ${tab === 'abtest' ? 'as-filter-tab--active' : ''}`} onClick={() => setTab('abtest')}>A/B Test</button>
            </div>
          </div>
          <button type="button" className="as-tabs-arrow as-tabs-arrow--right" onClick={() => scrollTabs(1)} disabled={atEnd} aria-label="Scroll tabs right">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </div>

      {tab === 'submissions' && <SubmissionsTab />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'pageAnalytics' && <CommunityAnalyticsTab />}
      {tab === 'communityStats' && <CommunityStatsTab />}
      {tab === 'signups' && <NewSignupsTab />}
      {tab === 'waitlist' && <QuickMatchWaitlistTab />}
      {tab === 'communities' && <ManageCommunitiesTab />}
      {tab === 'members' && <CommunityMembersTab />}
      {tab === 'abtest' && <ABTestTab />}
      {tab === 'email' && <EmailManagerTab />}
    </div>
  );
};

export default Admin;
